import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { AdminOverview, BackupStatus } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { formatDate, timeAgo } from '../../lib/format';
import { Alert, LoadingBlock } from '../../components/ui';
import { Stat } from '../../components/admin/AdminLayout';
import { AccountFlags } from './AdminAccountsPage';
import { MailWarnings, useMailStatus } from './AdminEmailPage';
import { UpdatesWaiting, useServerState } from './AdminServerPage';

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '–');

export function AdminOverviewPage() {
  const q = useQuery({ queryKey: ['admin', 'overview'], queryFn: () => api.get<AdminOverview>('/admin/overview') });
  const mail = useMailStatus();
  const server = useServerState();
  const backups = useQuery({ queryKey: ['admin', 'backups'], queryFn: () => api.get<BackupStatus>('/admin/backups'), refetchInterval: 5 * 60_000 });
  if (q.isLoading) return <LoadingBlock />;
  if (!q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const d = q.data;
  const max = Math.max(1, ...d.signupsByWeek.map((w) => w.count));
  const thisWeek = d.signupsByWeek.at(-1)?.count ?? 0;

  return (
    <>
      <h1 className="mb-5 text-[24px] font-semibold tracking-[-0.01em]">Overview</h1>
      <div className="mb-5 space-y-3 empty:hidden">
        {mail.data && <MailWarnings s={mail.data} linkToEmail />}
        {backups.data && <BackupWarning b={backups.data} />}
        {server.data && <UpdatesWaiting s={server.data} />}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Accounts" value={d.accounts.total} sub={`${d.accounts.headCoaches} head coaches`} />
        <Stat label="Active in the last 7 days" value={d.active.last7} sub={`${d.active.last30} in the last 30 days`} />
        <Stat label="Teams" value={d.teams.active} sub={`${d.teams.archived} archived`} />
        <Stat label="Sessions planned" value={d.sessions.total} sub={`${d.sessions.next30} in the next 30 days`} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Email confirmed" value={pct(d.accounts.confirmed, d.accounts.total)} sub={`${d.accounts.confirmed} of ${d.accounts.total}`} />
        <Stat label="Two-step sign-in on" value={pct(d.accounts.withMfa, d.accounts.total)} sub={`${d.accounts.withMfa} of ${d.accounts.total}`} />
        <Stat label="Suspended" value={d.accounts.suspended} />
        <Stat label="Sessions run in the last 30 days" value={d.sessions.last30} />
      </div>

      <section className="mt-8 grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div>
          <h2 className="mb-1 text-[16px] font-semibold">Sign-ups per week</h2>
          <p className="tnum mb-3 text-[14px] text-muted">{thisWeek} this week · last 12 weeks</p>
          <div className="rounded-lg border border-line bg-surface p-4">
            <div className="flex h-40 items-end gap-1.5" role="img" aria-label={`Sign-ups per week: ${d.signupsByWeek.map((w) => w.count).join(', ')}`}>
              {d.signupsByWeek.map((w) => (
                <div key={w.week} className="group relative flex h-full flex-1 flex-col justify-end" title={`Week of ${formatDate(w.week)}: ${w.count}`}>
                  <span className="tnum mb-1 text-center text-[11px] text-muted">{w.count || ''}</span>
                  <div className="rounded-t-sm bg-pink" style={{ height: `${(w.count / max) * 100}%`, minHeight: w.count ? 3 : 1, opacity: w.count ? 1 : 0.25 }} />
                </div>
              ))}
            </div>
            <div className="tnum mt-2 flex justify-between text-[11px] text-muted">
              <span>{formatDate(d.signupsByWeek[0]!.week)}</span>
              <span>This week</span>
            </div>
          </div>
        </div>
        <div>
          <h2 className="mb-1 text-[16px] font-semibold">Newest accounts</h2>
          <p className="mb-3 text-[14px] text-muted">
            <Link to="/admin/accounts" className="text-action hover:underline">
              See all accounts
            </Link>
          </p>
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
            {d.recentSignups.map((a) => (
              <li key={a.id}>
                <Link to={`/admin/accounts/${a.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-sunken">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{a.name}</p>
                    <p className="truncate text-[13px] text-muted">{a.email}</p>
                  </div>
                  <AccountFlags a={a} />
                  <span className="shrink-0 text-[13px] text-muted">{timeAgo(a.createdAt)}</span>
                </Link>
              </li>
            ))}
            {!d.recentSignups.length && <li className="px-4 py-6 text-center text-muted">No accounts yet.</li>}
          </ul>
        </div>
      </section>
      {backups.data && <BackupLine b={backups.data} />}
    </>
  );
}

const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Problems with the server's nightly backup. */
function BackupWarning({ b }: { b: BackupStatus }) {
  if (!b.configured || b.ok === null) return null;
  const how = 'On the server, run hoopify-server logs backup to see why, and hoopify-server backup to try again.';
  if (!b.ok) return <Alert>The last database backup{b.at ? ` (${timeAgo(b.at)})` : ''} had a problem: {b.message ?? 'no details'}. {how}</Alert>;
  if (b.stale) return <Alert tone="warn">The last database backup was {b.at ? timeAgo(b.at) : 'a while ago'}. Backups should run every night. {how}</Alert>;
  return null;
}

/** One line under the overview: when the database was last backed up. */
export function BackupLine({ b, compact = false }: { b: BackupStatus; compact?: boolean }) {
  if (!b.configured) return null;
  const cls = `${compact ? 'mt-1' : 'mt-6'} text-[13px] text-muted`;
  if (b.ok === null) return <p className={cls}>Database backups run every night. The first one hasn’t run yet.</p>;
  return (
    <p className={cls}>
      Last database backup: {b.at ? `${timeAgo(b.at)} (${formatDate(b.at)})` : 'unknown'}
      {b.bytes ? `, ${kb(b.bytes)}` : ''}
      {b.offsite === 'ok' ? ', copied off-site' : b.offsite === 'off' ? ', kept on the server only (off-site copies aren’t set up)' : ''}.
    </p>
  );
}

